import { CalendarJobPostingItemVO, CalendarPcItemVO, CalendarPcListItemVO } from '@domain/vo/calendar/CalendarPcListVO';
import { JobPostStatusType } from '@domain/constant/JobPostStatusType';
import DateFormat from '@utils/date/format';

interface useHandleCalendarDataProps {
  calendarDate: Date;
  bookmarkYn: boolean;
  jobPostStatus: JobPostStatusType | null;
  aiScreeningYn: boolean;
}

const useHandleCalendarData = ({
  calendarDate,
  bookmarkYn,
  jobPostStatus,
  aiScreeningYn,
}: useHandleCalendarDataProps) => {
  const getAllJobPostingList = (calendarItems?: CalendarPcListItemVO[]) => {
    const filteredCalendarItemWithDate = getWholeJobPostingDataInMonth(calendarItems);
    const jobPostingItemList = flatCompanies(filteredCalendarItemWithDate);
    const filteredJobPosting = filterJobPostingsByAttributes(jobPostingItemList);
    const allJobPostingList = convertJobPostingsToJobPostModalVO(filteredJobPosting);
    return allJobPostingList;
  };

  //해당 월에 포함된 모든 공고 반환 함수
  const getWholeJobPostingDataInMonth = (calendarItems?: CalendarPcListItemVO[]) => {
    if (!calendarItems) return [];
    const monthDays = DateFormat.getWeeksOfMonth(calendarDate);
    return calendarItems.filter((item) => monthDays.includes(item.date));
  };

  //jobPosting 배열을 반환
  const flatCompanies = (list: CalendarPcListItemVO[]) => {
    return list.flatMap((item) => {
      return item.companies.flatMap((company) => {
        return company.jobPostings;
      });
    });
  };

  //역검 활용 공고,북마크, 공고 상태로 calendarData filter
  const filterJobPosting = (jobPosting: CalendarJobPostingItemVO, statusType?: JobPostStatusType) => {
    const bookmarkMatch = !bookmarkYn || jobPosting.bookmarkYn === bookmarkYn;
    const statusMatch = !jobPostStatus || statusType === jobPostStatus;
    const aiScreeningMatch = !aiScreeningYn || jobPosting.aiScreeningYn === aiScreeningYn;
    return bookmarkMatch && statusMatch && aiScreeningMatch;
  };

  //JobPostingItemVO를 역검 활용 공고,북마크,bookMark와 statusType으로 필터
  const filterJobPostingsByAttributes = (list: CalendarJobPostingItemVO[]) => {
    return list.filter((item) => filterJobPosting(item, item?.statusType));
  };

  //jobPosting 객체를 jobPostModalVO로 변환
  const convertJobPostingsToJobPostModalVO = (jobPostings: CalendarJobPostingItemVO[]) => {
    return jobPostings.map((jobPosting) => {
      const itemKey = `${jobPosting.sn}-${jobPosting.statusType}`;
      const jobPostModalVO = {
        key: itemKey,
        sn: jobPosting.sn,
        name: jobPosting.companyName || '',
      };
      return jobPostModalVO;
    });
  };

  const getFilteredCalendarData = (list?: CalendarPcListItemVO[]) => {
    if (!list) return [];

    return list.reduce((acc: CalendarPcListItemVO[], item) => {
      const newItem = filterCompaniesHasJobPostings(item);
      if (newItem.companies.length > 0) {
        acc.push(newItem);
      }
      return acc;
    }, []);
  };

  // jobPosting이 없는 company를 제외
  const filterCompaniesHasJobPostings = (item: CalendarPcListItemVO): CalendarPcListItemVO => {
    const newCompanies = item.companies.reduce((acc: CalendarPcItemVO[], company) => {
      const updatedCompany = getFilteredJobPosting(company);
      if (updatedCompany.jobPostings.length > 0) {
        acc.push(updatedCompany);
      }
      return acc;
    }, []);

    return {
      ...item,
      companies: newCompanies,
    };
  };

  // jobPosting filtering
  const getFilteredJobPosting = (company: CalendarPcItemVO) => {
    const newJobPostings = company.jobPostings.filter((jobPosting) =>
      filterJobPosting(jobPosting, jobPosting.statusType),
    );

    return {
      ...company,
      jobPostings: newJobPostings,
    };
  };

  return {
    getAllJobPostingList,
    getFilteredCalendarData,
  };
};

export default useHandleCalendarData;
